import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { YMaps, Map, Placemark } from '@pbe/react-yandex-maps'
import {
	Button,
	Dialog, DialogContent, DialogTitle,
	IconButton,
	Spinner,
} from '@rentflowdev/ui'
import { XIcon } from 'lucide-react'
import { isPreciseEnough, resolveGeoObject, type GeoObjectLike } from '@/lib/geo-address'
import { IS_MAPS_CONFIGURED, YANDEX_MAPS_API_KEY } from '@/lib/maps-config'
const DEFAULT_CENTER: [number, number] = [55.751244, 37.618423]
const DEFAULT_ZOOM = 11

export interface MapPickerResult {
	latitude:  number
	longitude: number
	city?:     string
	address?:  string
}

interface MapPickerProps {
	open: boolean
	onOpenChange: (open: boolean) => void
	initialLat?: number
	initialLng?: number
	onSelect: (result: MapPickerResult) => void
	/** Land plots may stop at street level; everything else needs a building. */
	allowStreetLevel?: boolean
}

interface YmapsGeocodeResult {
	geoObjects: { get: (i: number) => GeoObjectLike | undefined }
}

interface YmapsApi {
	geocode: (point: [number, number], opts?: { results?: number }) => Promise<YmapsGeocodeResult>
}

export function MapPicker({ open, onOpenChange, initialLat, initialLng, onSelect, allowStreetLevel }: MapPickerProps) {
	const { t } = useTranslation()
	const [point, setPoint] = useState<[number, number] | null>(
		initialLat != null && initialLng != null ? [initialLat, initialLng] : null,
	)
	const [resolving, setResolving] = useState(false)
	const [notice, setNotice]       = useState<string | null>(null)
	const ymapsRef = useRef<YmapsApi | null>(null)

	const initialCenter = point ?? DEFAULT_CENTER

	/**
	 * Reads the geocoder through the same `resolveGeoObject`/`isPreciseEnough`
	 * path as the address suggester. Before, this dialog parsed the response
	 * itself and applied no precision check at all, so anyone blocked by the
	 * suggester's "pick a building" rule could pin the map instead and store a
	 * district-level address with coordinates hundreds of metres off — and a
	 * failed reverse geocode emitted bare coordinates with no address.
	 */
	async function handleConfirm() {
		if (!point) return
		setResolving(true)
		setNotice(null)
		try {
			const ymaps = ymapsRef.current
			if (!ymaps) throw new Error('ymaps not loaded')
			const res      = await ymaps.geocode(point, { results: 1 })
			const resolved = resolveGeoObject(res.geoObjects.get(0))

			if (!resolved) {
				setNotice(t('properties.form.addressGeocodeFailed'))
				return
			}
			if (!isPreciseEnough(resolved, allowStreetLevel)) {
				setNotice(t('properties.form.addressNeedsHouse'))
				return
			}

			onSelect({
				// The pin the user placed stays authoritative; the geocoder only
				// supplies the labels for it.
				latitude:  point[0],
				longitude: point[1],
				city:      resolved.city,
				address:   resolved.address,
			})
			onOpenChange(false)
		} catch (err) {
			console.warn('[MapPicker] reverse geocode failed:', err)
			setNotice(t('properties.form.addressGeocodeFailed'))
		} finally {
			setResolving(false)
		}
	}

	if (!IS_MAPS_CONFIGURED) {
		return (
			<Dialog open={open} onOpenChange={onOpenChange}>
				<DialogContent className="sm:max-w-md">
					<DialogTitle>{t('properties.form.showOnMap')}</DialogTitle>
					<p className="text-sm text-(--color-muted-foreground)">
						{t('properties.form.mapNotConfigured')}
					</p>
				</DialogContent>
			</Dialog>
		)
	}

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent
				showCloseButton={false}
				className="sm:max-w-3xl w-full p-0 gap-0 rounded-lg overflow-hidden"
			>
				<div className="flex items-center justify-between border-b border-(--color-border) p-4">
					<DialogTitle className="text-lg font-[500]">
						{t('properties.form.showOnMap')}
					</DialogTitle>
					<IconButton
						variant="ghost"
						size="sm"
						type="button"
						onClick={() => onOpenChange(false)}
						aria-label={t('common.close')}
					>
						<XIcon />
					</IconButton>
				</div>

				<div className="h-[60vh]">
					<YMaps query={{ apikey: YANDEX_MAPS_API_KEY, lang: 'ru_RU', load: 'package.full' }}>
						<Map
							defaultState={{ center: initialCenter, zoom: DEFAULT_ZOOM }}
							width="100%"
							height="100%"
							onLoad={(ymaps) => { ymapsRef.current = ymaps as unknown as YmapsApi }}
							onClick={(e: { get: (k: string) => [number, number] }) => {
								const coords = e.get('coords')
								if (coords) setPoint(coords)
							}}
						>
							{point && <Placemark geometry={point} />}
						</Map>
					</YMaps>
				</div>

				<div className="flex items-center justify-end gap-2 border-t border-(--color-border) p-4">
					{notice && (
						<p className="mr-auto text-sm text-(--color-destructive)">{notice}</p>
					)}
					<Button variant="secondary" onClick={() => onOpenChange(false)}>
						{t('common.close')}
					</Button>
					<Button disabled={!point || resolving} onClick={() => void handleConfirm()}>
						{resolving ? <Spinner size="sm" /> : t('properties.form.confirmLocation')}
					</Button>
				</div>
			</DialogContent>
		</Dialog>
	)
}
